import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { RequestTypeCreateModal } from './request-type-create-modal';

import { GridTypeahead } from '@/components/common/grid-typeahead';
import { Button, Modal, Text, TextInput, useTheme } from '@/components/ui';
import { useCreateRequestType, useRequestTypes } from '@/hooks/use-data';
import { useAdminOrFlagged } from '@/hooks/use-permissions';
import type { ExteriorApplicationDetail } from '@/services/data-service';
import { FeatureFlagName } from '@/types/models';

/**
 * Correct a request (#369).
 *
 * Everything on a request was captured by a parser reading a homeowner's form, so
 * everything on it can be wrong. The TYPE most of all: a driveway replacement arrives
 * classified as Cameras because that is the request type the form's wording happened to
 * match, and until it is fixed the request carries the wrong submission requirements, the
 * wrong review clock and the wrong wording in the board packet.
 *
 * Changing the type is therefore not a cosmetic relabel — it swaps the CHECKLIST the
 * request is measured against and re-derives the review deadline from the new type's days,
 * which is why the dialog says so out loud rather than letting the numbers move silently.
 */
export interface ApplicationEditModalProps {
	application: ExteriorApplicationDetail | null;
	busy?: boolean;
	onDismiss: () => void;
	onSave: (values: {
		requestTypeId: string;
		submitterName: string | null;
		submitterEmail: string | null;
		submitterPhone: string | null;
		propertyAddress: string | null;
		description: string | null;
	}) => void;
}

export const ApplicationEditModal: React.FC<ApplicationEditModalProps> = ({
	application, busy = false, onDismiss, onSave,
}) => {
	const theme = useTheme();
	const { data: requestTypes } = useRequestTypes(application?.associationId ?? '', {
		enabled: !!application?.associationId,
	});

	const [requestTypeId, setRequestTypeId] = useState('');
	const [submitterName, setSubmitterName] = useState('');
	const [submitterEmail, setSubmitterEmail] = useState('');
	const [submitterPhone, setSubmitterPhone] = useState('');
	const [propertyAddress, setPropertyAddress] = useState('');
	const [description, setDescription] = useState('');
	const [creatingType, setCreatingType] = useState(false);
	const createType = useCreateRequestType();
	// Creating a type edits the SHARED ruleset, which `POST /application-rulesets/request-types`
	// gates on `isAdminOrFlagged(application_rules_admin)`. Hiding the option for everyone else
	// beats a 403 after they have filled the form in.
	const { allowed: canCreateType } = useAdminOrFlagged(FeatureFlagName.ApplicationRulesAdmin);

	// Re-seed per request, or the form shows the previously opened one's values.
	useEffect(() => {
		if (!application) return;
		setRequestTypeId(application.requestTypeId ?? '');
		setSubmitterName(application.submitterName ?? '');
		setSubmitterEmail(application.submitterEmail ?? '');
		setSubmitterPhone(application.submitterPhone ?? '');
		setPropertyAddress(application.propertyAddress ?? '');
		setDescription(application.description ?? '');
	}, [application]);

	// Memoized: GridTypeahead maps over `items` on every render, and an inline array
	// would rebuild the option list each keystroke.
	const typeOptions = useMemo(
		() => (requestTypes ?? []) as Array<{ id: string; name: string }>,
		[requestTypes],
	);
	const typeChanged = !!application && requestTypeId !== application.requestTypeId;

	return (
		<Modal visible={application != null} onDismiss={onDismiss}>
			<View style={{ padding: 20, width: 520, maxWidth: '100%' }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 12 }}>Edit request</Text>

				<ScrollView style={{ maxHeight: 460 }}>
					<Text style={{ fontWeight: '700', marginBottom: 6 }}>Request type</Text>
					{/*
						A typeahead, not a chip group: this ruleset carries 22 types and a
						community with a fuller one carries more. Twenty-two chips is a wall to
						read, and the reviewer already knows the word they are looking for —
						"blacktop", "fence" — so let them type it.

						`showAllOnEmpty` because the vocabulary is closed and browsable: opening
						the field with nothing typed should show what is on offer, which is how
						someone discovers the type they did not know existed.
					*/}
					<View
						style={{
							borderWidth: 1,
							borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							borderRadius: 8, paddingHorizontal: 10, paddingVertical: 2,
						}}
					>
						<GridTypeahead
							items={typeOptions}
							toOption={(type) => ({ id: type.id, display: type.name })}
							value={requestTypeId || null}
							onSelect={(type) => setRequestTypeId(type?.id ?? '')}
							placeholder="Search request types…"
							showAllOnEmpty
							testID="request-type-typeahead"
						/>
					</View>
					{/*
						The escape hatch. Homeowners ask for things nobody wrote a rule for, and
						without this the only options are mis-filing under something close or
						Uncategorized — which carries no requirements, so every future one lands
						the same way.
					*/}
					{canCreateType ? (
						<Pressable
							onPress={() => setCreatingType(true)}
							testID="btn-new-request-type"
							style={{ alignSelf: 'flex-start', paddingVertical: 8, paddingHorizontal: 4 }}
						>
							<Text style={{ color: theme.colors.primary, fontWeight: '600', fontSize: 12 }}>
								＋ Nothing matches — add a new type
							</Text>
						</Pressable>
					) : null}
					{typeChanged ? (
						<Text style={{ fontSize: 12, color: theme.colors.error, marginTop: 4 }} testID="type-change-warning">
							Changing the type swaps the submission requirements and re-dates the review deadline.
						</Text>
					) : null}

					<Text style={{ fontWeight: '700', marginTop: 16, marginBottom: 4 }}>Description</Text>
					<TextInput
						value={description}
						onChangeText={setDescription}
						multiline
						numberOfLines={4}
						placeholder="What the homeowner is asking to do"
						testID="application-description-input"
					/>

					<Text style={{ fontWeight: '700', marginTop: 14, marginBottom: 4 }}>Property address</Text>
					<TextInput value={propertyAddress} onChangeText={setPropertyAddress} testID="application-address-input" />

					<Text style={{ fontWeight: '700', marginTop: 14, marginBottom: 4 }}>Submitter</Text>
					<TextInput value={submitterName} onChangeText={setSubmitterName} placeholder="Name" testID="application-submitter-input" />
					<View style={{ height: 8 }} />
					<TextInput value={submitterEmail} onChangeText={setSubmitterEmail} placeholder="Email" testID="application-email-input" />
					<View style={{ height: 8 }} />
					<TextInput value={submitterPhone} onChangeText={setSubmitterPhone} placeholder="Phone" testID="application-phone-input" />
				</ScrollView>

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
					<Button mode="outlined" onPress={onDismiss}>Cancel</Button>
					<Button
						mode="contained"
						loading={busy}
						disabled={busy || !requestTypeId}
						testID="btn-save-application-edit"
						onPress={() => onSave({
							requestTypeId,
							// Empty means "cleared", not "unchanged" — a wrong phone number has
							// to be removable, not just replaceable.
							submitterName: submitterName.trim() || null,
							submitterEmail: submitterEmail.trim() || null,
							submitterPhone: submitterPhone.trim() || null,
							propertyAddress: propertyAddress.trim() || null,
							description: description.trim() || null,
						})}
					>
						Save
					</Button>
				</View>
			</View>
			<RequestTypeCreateModal
				visible={creatingType}
				busy={createType.isPending}
				onDismiss={() => setCreatingType(false)}
				onCreate={async (values) => {
					try {
						const created = await createType.mutateAsync(values);
						// Select it immediately — the reviewer created it BECAUSE they wanted
						// this request to be it.
						setRequestTypeId(created.id);
						setCreatingType(false);
					} catch {
						// The dialog stays open with the draft intact so a duplicate slug or a
						// permission error can be corrected rather than retyped.
					}
				}}
			/>
		</Modal>
	);
};

export default ApplicationEditModal;
